import { useCallback } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';

import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { OptionsEditor } from '../../../shared/ui/forms/OptionsEditor';
import {
  countNonEmptyOptions,
  MIN_OPTIONS,
} from '../../../shared/domain/optionsValidation';
import { SubQuestionRequirementToggle } from './SubQuestionRequirementToggle';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';
import { isEditableOptionType } from '../../../shared/domain/subQuestionResponseType';
import type { ResponseType } from '../../../../types/helios/Question';
import type { SubQuestion } from '../logic/reviewTypes';

/** Empty options list — module-level for reference stability when a sub has no
 *  options yet. */
const EMPTY_OPTIONS: readonly string[] = [];

export interface SubQuestionDataCollectionFieldsProps {
  readonly sub: SubQuestion;
  /** The sub's response type — always a data-collection type here (the row
   *  only renders this body for data-collection subs). */
  readonly responseType: ResponseType;
  readonly disabled: boolean;
  /** Patches the sub-question and hands it up. */
  readonly patch: (fields: Partial<SubQuestion>) => void;
  readonly dataTestId?: string;
}

/**
 * The answer-config body for a DATA-COLLECTION sub-question: an author-editable
 * options list (for Single/Multi Select only — Yes/No auto-fills, Text/Numeric/
 * Employee-login need none) plus the two unconditional requirement toggles
 * (`requireComment`, `requireAttachment`). Rendered by {@link SubQuestionRow}
 * in place of the four scored Pass/Fail toggles when the response type is a
 * data-collection type.
 *
 * Controlled — every edit flows through `patch`.
 */
export function SubQuestionDataCollectionFields({
  sub,
  responseType,
  disabled,
  patch,
  dataTestId,
}: SubQuestionDataCollectionFieldsProps): JSX.Element {
  const { t } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);

  const handleOptionsChange = useCallback(
    (options: string[]) => patch({ options }),
    [patch],
  );
  const handleRequireComment = useCallback(
    (checked: boolean) => patch({ requireComment: checked }),
    [patch],
  );
  const handleRequireAttachment = useCallback(
    (checked: boolean) => patch({ requireAttachment: checked }),
    [patch],
  );

  const infoAltText = t('subInfoAltText');
  const showOptionsEditor = isEditableOptionType(responseType);
  // Invalid when an editable select type has fewer than the required options.
  // Same measure the publish gate + row "Incomplete" badge use, so the field
  // error, the badge, and the banner never disagree. Only meaningful while the
  // options editor is shown (editable select types).
  const optionsBelowMin =
    showOptionsEditor && countNonEmptyOptions(sub.options ?? EMPTY_OPTIONS) < MIN_OPTIONS;

  return (
    <Col gridGap="dimensions.spacing.300" width="100%">
      {showOptionsEditor ? (
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={optionsBelowMin}>
            {t('subOptionsLabel')}
          </RequiredLabel>
          <OptionsEditor
            value={sub.options ?? EMPTY_OPTIONS}
            onChange={handleOptionsChange}
            labels={{
              optionPlaceholder: t('subOptionPlaceholder'),
              optionAriaLabel: t('subOptionAriaLabel'),
              addButton: t('subOptionAddButton'),
              removeAltText: t('subOptionRemoveAltText'),
              minOptionsText: t('subOptionsMinText'),
            }}
            disabled={disabled}
            hasError={optionsBelowMin}
            {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-options` } : {})}
          />
        </Col>
      ) : null}

      <Row gridGap="dimensions.spacing.400" flexWrap="wrap">
        <View flex={1} minWidth={0}>
          <SubQuestionRequirementToggle
            label={t('subRequireCommentLabel')}
            info={t('subRequireCommentInfo')}
            infoAltText={infoAltText}
            checked={sub.requireComment ?? false}
            onChange={handleRequireComment}
            disabled={disabled}
            trackingId={`audit-whiz.review.sub-q.${sub.orderIndex}.require-comment`}
            {...(dataTestId !== undefined
              ? { dataTestId: `${dataTestId}-require-comment` }
              : {})}
          />
        </View>
        <View flex={1} minWidth={0}>
          <SubQuestionRequirementToggle
            label={t('subRequireAttachmentLabel')}
            info={t('subRequireAttachmentInfo')}
            infoAltText={infoAltText}
            checked={sub.requireAttachment ?? false}
            onChange={handleRequireAttachment}
            disabled={disabled}
            trackingId={`audit-whiz.review.sub-q.${sub.orderIndex}.require-attachment`}
            {...(dataTestId !== undefined
              ? { dataTestId: `${dataTestId}-require-attachment` }
              : {})}
          />
        </View>
      </Row>
    </Col>
  );
}
